import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';

const fmt = v => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v||0);

// Pantalla para revisar con qué producto de la lista de precios cruzó cada producto
// de la tienda. El emparejamiento automático acierta la mayoría, pero como los códigos
// del PDF no coinciden con los de la web algunos quedan mal; aquí se corrigen a mano y
// quedan fijos: las importaciones siguientes ya no los vuelven a adivinar.
export default function RevisarCostosPDF() {
  const [datos, setDatos] = useState({ productos: [], resumen: {} });
  const [estado, setEstado] = useState('dudosos');
  const [cargando, setCargando] = useState(true);
  const [corrigiendo, setCorrigiendo] = useState(null);   // producto en edición
  const [opciones, setOpciones] = useState([]);
  const [buscarPdf, setBuscarPdf] = useState('');
  const [msg, setMsg] = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get(`/api/catalogo/cruce-pdf?proveedor=Tecnoplaza&estado=${estado}`);
      setDatos(r.data);
    } catch (e) { setMsg('No se pudo cargar: ' + (e.response?.data?.error || e.message)); }
    setCargando(false);
  }, [estado]);

  useEffect(() => { cargar(); }, [cargar]);

  // Al abrir el corrector se precarga la búsqueda con el nombre del producto, que
  // suele ser suficiente para encontrar el correcto en la lista del proveedor.
  const abrirCorrector = async (p) => {
    setCorrigiendo(p);
    const termino = p.nombre.split(' ').slice(0, 2).join(' ');
    setBuscarPdf(termino);
    buscarEnPdf(termino);
  };

  const buscarEnPdf = async (q) => {
    try {
      const r = await api.get(`/api/catalogo/pdf-sobrantes?proveedor=Tecnoplaza&buscar=${encodeURIComponent(q)}`);
      setOpciones(r.data);
    } catch (e) { setOpciones([]); }
  };

  const aplicarCorreccion = async (opcion) => {
    try {
      await api.put(`/api/catalogo/${corrigiendo.id}/cruce-pdf`, {
        pdf_nombre_origen: opcion.nombre, precio_pdf: opcion.precio_pdf,
      });
      setMsg(`✓ Corregido: ${corrigiendo.nombre.slice(0, 40)}`);
      setCorrigiendo(null); cargar();
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const quitar = async (p) => {
    if (!window.confirm(`¿Quitar el costo del PDF de "${p.nombre.slice(0,50)}"?\n\nEl producto sigue a la venta, pero sin costo real hasta la próxima importación.`)) return;
    try {
      await api.put(`/api/catalogo/${p.id}/cruce-pdf`, { quitar: true });
      setMsg('Costo quitado'); cargar();
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  // Confirmar deja el cruce fijo aunque el algoritmo lo hubiera cambiado después
  const confirmar = async (p) => {
    try {
      await api.put(`/api/catalogo/${p.id}/cruce-pdf`, {
        pdf_nombre_origen: p.pdf_nombre_origen, precio_pdf: p.precio_pdf,
      });
      setMsg('✓ Confirmado'); cargar();
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const R = datos.resumen || {};
  const pct = R.total ? Math.round(R.cruzados / R.total * 100) : 0;

  const TABS = [
    { k:'dudosos', t:`⚠️ Por revisar (${R.dudosos ?? 0})` },
    { k:'sin',     t:'Sin costo del PDF' },
    { k:'ok',      t:'Con costo real' },
    { k:'todos',   t:'Todos' },
  ];

  return (
    <Layout>
      <div style={{ padding:'22px 26px' }}>
        <h1 style={{ fontSize:'20px', fontWeight:'700', color:'#fff', marginBottom:'4px' }}>
          🔍 Revisar costos de la lista de precios
        </h1>
        <p style={{ fontSize:'13px', color:'rgba(255,255,255,0.45)', marginBottom:'18px', maxWidth:'760px', lineHeight:'1.55' }}>
          Los códigos del PDF de Tecnoplaza no coinciden con los de su página web, así que el
          sistema empareja cada producto por nombre y verifica que el precio tenga sentido.
          Aquí puedes ver con qué producto del PDF cruzó cada uno y corregir los que estén mal.
          <b style={{ color:'rgba(255,255,255,0.7)' }}> Lo que corrijas queda fijo</b> y no se vuelve a cambiar solo.
        </p>

        <div style={{ display:'flex', gap:'12px', marginBottom:'18px', flexWrap:'wrap' }}>
          {[
            { l:'Con costo real', v:`${R.cruzados ?? 0} de ${R.total ?? 0}`, sub:`${pct}%`, c:'#22C55E' },
            { l:'Por revisar',    v:R.dudosos ?? 0,  sub:'poca certeza', c:'#FBBF24' },
            { l:'Confirmados por ti', v:R.revisados ?? 0, sub:'fijos', c:'#60A5FA' },
          ].map(x => (
            <div key={x.l} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'11px', padding:'13px 18px', minWidth:'150px' }}>
              <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.4)', marginBottom:'3px' }}>{x.l}</div>
              <div style={{ fontSize:'19px', fontWeight:'700', color:x.c }}>{x.v}</div>
              <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.3)' }}>{x.sub}</div>
            </div>
          ))}
        </div>

        {msg && <div style={{ background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.25)', color:'#86EFAC', padding:'10px 14px', borderRadius:'8px', marginBottom:'14px', fontSize:'13px' }}>{msg}</div>}

        <div style={{ display:'flex', gap:'8px', marginBottom:'14px', flexWrap:'wrap' }}>
          {TABS.map(t => (
            <button key={t.k} onClick={()=>setEstado(t.k)} style={{
              padding:'7px 14px', borderRadius:'8px', fontSize:'12.5px', cursor:'pointer',
              border: estado===t.k ? '1px solid rgba(99,102,241,0.5)' : '1px solid rgba(255,255,255,0.1)',
              background: estado===t.k ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.03)',
              color: estado===t.k ? '#A5B4FC' : 'rgba(255,255,255,0.55)', fontWeight: estado===t.k?'600':'400',
            }}>{t.t}</button>
          ))}
        </div>

        {cargando ? <div style={{ color:'rgba(255,255,255,0.4)', padding:'30px' }}>Cargando…</div> : (
          <div style={{ display:'grid', gap:'9px' }}>
            {datos.productos.length === 0 && (
              <div style={{ color:'rgba(255,255,255,0.35)', padding:'26px', textAlign:'center', background:'rgba(255,255,255,0.02)', borderRadius:'10px' }}>
                No hay productos en esta lista.
              </div>
            )}
            {datos.productos.map(p => (
              <div key={p.id} style={{
                background:'rgba(255,255,255,0.03)', borderRadius:'10px', padding:'13px 15px',
                border: p.pdf_revisado ? '1px solid rgba(96,165,250,0.3)'
                      : !p.pdf_nombre_origen ? '1px solid rgba(255,255,255,0.06)'
                      : p.pdf_confianza < 0.75 ? '1px solid rgba(251,191,36,0.3)'
                      : '1px solid rgba(34,197,94,0.2)',
              }}>
                <div style={{ display:'flex', justifyContent:'space-between', gap:'14px', flexWrap:'wrap' }}>
                  <div style={{ flex:'1 1 380px', minWidth:0 }}>
                    <div style={{ fontSize:'13px', color:'#fff', fontWeight:'600', marginBottom:'5px' }}>
                      {p.pdf_revisado && <span title="Corregido por ti" style={{ color:'#60A5FA' }}>📌 </span>}
                      {p.nombre}
                    </div>
                    {p.pdf_nombre_origen ? (
                      <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.45)', lineHeight:'1.5' }}>
                        <span style={{ color:'rgba(255,255,255,0.3)' }}>En el PDF: </span>
                        {p.pdf_nombre_origen}
                        <div style={{ marginTop:'3px' }}>
                          <span style={{ color:'rgba(255,255,255,0.3)' }}>{p.pdf_metodo}</span>
                          {p.pdf_confianza != null && !p.pdf_revisado && (
                            <span style={{ color: p.pdf_confianza < 0.75 ? '#FBBF24' : '#4ADE80', marginLeft:'8px' }}>
                              certeza {Math.round(p.pdf_confianza * 100)}%
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.3)', fontStyle:'italic' }}>
                        No se encontró en la lista de precios — se usa el precio de la página web como costo.
                      </div>
                    )}
                  </div>

                  <div style={{ textAlign:'right', fontSize:'12px', flexShrink:0 }}>
                    <div style={{ color:'rgba(255,255,255,0.4)' }}>web {fmt(p.precio_costo)}</div>
                    {p.precio_pdf ? (
                      <>
                        <div style={{ color:'#4ADE80', fontWeight:'600' }}>costo {fmt(p.precio_pdf)}</div>
                        <div style={{ color:'rgba(255,255,255,0.35)' }}>−{p.pdf_descuento}%</div>
                      </>
                    ) : <div style={{ color:'rgba(255,255,255,0.25)' }}>sin costo</div>}
                    <div style={{ color:'rgba(255,255,255,0.5)', marginTop:'2px' }}>venta {fmt(p.precio_venta)}</div>
                  </div>
                </div>

                <div style={{ display:'flex', gap:'7px', marginTop:'10px', flexWrap:'wrap' }}>
                  <button onClick={()=>abrirCorrector(p)} style={btn('#A5B4FC','rgba(99,102,241,0.12)','rgba(99,102,241,0.3)')}>
                    ✏️ {p.pdf_nombre_origen ? 'Cambiar' : 'Buscar en el PDF'}
                  </button>
                  {p.pdf_nombre_origen && !p.pdf_revisado && (
                    <button onClick={()=>confirmar(p)} style={btn('#86EFAC','rgba(34,197,94,0.12)','rgba(34,197,94,0.3)')}>
                      ✓ Está bien
                    </button>
                  )}
                  {p.pdf_nombre_origen && (
                    <button onClick={()=>quitar(p)} style={btn('rgba(255,255,255,0.5)','rgba(255,255,255,0.04)','rgba(255,255,255,0.1)')}>
                      Quitar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {corrigiendo && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', zIndex:100 }}>
            <div style={{ background:'#0F1629', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'14px', padding:'24px', width:'100%', maxWidth:'700px', maxHeight:'85vh', display:'flex', flexDirection:'column' }}>
              <h3 style={{ fontSize:'15px', color:'#fff', fontWeight:'700', marginBottom:'4px' }}>
                Buscar el producto correcto en la lista de precios
              </h3>
              <p style={{ fontSize:'12.5px', color:'rgba(255,255,255,0.45)', marginBottom:'14px' }}>
                Producto de la tienda: <b style={{ color:'rgba(255,255,255,0.75)' }}>{corrigiendo.nombre}</b>
                <br/>Precio en la web: {fmt(corrigiendo.precio_costo)}
              </p>
              <input autoFocus value={buscarPdf}
                onChange={e => { setBuscarPdf(e.target.value); buscarEnPdf(e.target.value); }}
                placeholder="Escribe parte del nombre o la referencia…"
                style={{ width:'100%', padding:'10px 12px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.04)', color:'#fff', fontSize:'13px', marginBottom:'12px', boxSizing:'border-box' }}/>
              <div style={{ overflowY:'auto', flex:1, display:'grid', gap:'6px' }}>
                {opciones.length === 0 && (
                  <div style={{ color:'rgba(255,255,255,0.3)', fontSize:'12.5px', padding:'16px', textAlign:'center' }}>
                    Sin resultados. Prueba con menos palabras o con la referencia del modelo.
                  </div>
                )}
                {opciones.map(o => {
                  const caro = Number(o.precio_pdf) > Number(corrigiendo.precio_costo);
                  return (
                    <button key={o.id} onClick={()=>aplicarCorreccion(o)} style={{
                      textAlign:'left', padding:'10px 12px', borderRadius:'8px', cursor:'pointer',
                      background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)',
                      color:'#fff', fontSize:'12.5px', display:'flex', justifyContent:'space-between', gap:'12px',
                    }}>
                      <span style={{ flex:1, minWidth:0 }}>{o.nombre}</span>
                      <span style={{ flexShrink:0, color: caro ? '#FCA5A5' : '#4ADE80', fontWeight:'600' }}>
                        {fmt(o.precio_pdf)}{caro && ' ⚠️'}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop:'14px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:'11.5px', color:'rgba(255,255,255,0.3)' }}>
                  ⚠️ = más caro que el precio de la página web (probablemente no es el mismo producto)
                </span>
                <button onClick={()=>setCorrigiendo(null)} style={btn('rgba(255,255,255,0.6)','rgba(255,255,255,0.05)','rgba(255,255,255,0.12)')}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

const btn = (color, bg, border) => ({
  padding:'6px 12px', borderRadius:'7px', fontSize:'12px', cursor:'pointer',
  background:bg, border:`1px solid ${border}`, color, fontWeight:'500',
});
